import React, { useState } from "react";
import moment from "moment";
import styled from "styled-components";
import { UserIcon } from "ui/icons";
import { defaultThemeColors, fonts, mediaSizes, themeColor } from "ui/theme";
import { ButtonPrimary } from "../Button/ButtonStyled";
import { StarIcon } from "ui/icons/inputIcons/StarIcon";
import { ClassModal } from "./components/ClassModal";

import { RescheduleTooltip } from "./components/RescheduleTooltip";
import { RescheduleModal } from "./components/RescheduleModal";
import { Avatar } from "ui/base/editAvatar/Avatar";
import { ClassDateCard } from "../dateCard/ClassDateCard";
import { useNavigate } from "react-router-dom";
import { userRoute } from "pages/user/user.routes";

export const DEFAULT_TIMEZONE = "America/New_York";

export const UpcomingClassCard = ({ upcomingClass, isLoading, onCancel, sendMessage }) => {
    const [isOpenRescheduleModal, setIsOpenRescheduleModal] = useState(false);
    const navigate = useNavigate()

    const isRescheduled = upcomingClass.booking_slot_status === "rescheduled";

    const momentDateTime = moment.tz(
        `${upcomingClass.class_date} ${upcomingClass.class_time}`,
        "YYYY-MM-DD HH:mm:ss",
        upcomingClass.time_zone || DEFAULT_TIMEZONE
    );

    const class_type = upcomingClass?.class_type
        ? upcomingClass?.class_type.toLowerCase()
        : "";

    const isOnline = upcomingClass?.online_type === "online";

    const badgeVariant = isOnline
        ? "black_fade_small"
        : class_type === "private"
            ? "primary_fade_small"
            : "destructive_fade_small"

    const classDate = {
        type: isOnline
            ? upcomingClass?.online_type
            : upcomingClass?.class_type,
        date: momentDateTime.format("MMM D, YYYY"),
        time: momentDateTime.format("h:mm a"),
        cost: upcomingClass.total_amount,
    }

    const handleNavigation = () => {
        if (!upcomingClass?.teacher_user_id) return;

        navigate(`${userRoute.classDetail}${upcomingClass?.teacher_user_id}`)
    }

    return (
        <>
            <ClassItem>
                <ClassInfo>
                    <Avatar
                        profile={upcomingClass?.teacher_profile_pic}
                        alt={upcomingClass?.teacher_first_name}
                        onClick={handleNavigation}
                    />

                    <InfoContainer>
                        <Info>
                            <NameIconTextWrapper onClick={handleNavigation}>
                                <Name>{upcomingClass?.teacher_first_name}</Name>
                                <IconText>
                                    <StarIcon color="#FF827A" /> {upcomingClass.rating}
                                </IconText>
                            </NameIconTextWrapper>
                            <Details>
                                <span>
                                    <UserIcon color="placeholder" width="15" height="15" />
                                </span>
                                <span>{upcomingClass?.student_per_class}</span> |{" "}
                                <span>{upcomingClass?.class}</span>
                            </Details>

                            {isOnline ?
                                <ZoomLinkContainer>
                                    <ClassLinkLabel>{"Class link :"}</ClassLinkLabel>
                                    <ZoomLinkLabel href={upcomingClass?.class_link} target="_blank"
                                        rel="noopener noreferrer">{"Zoom Link"}</ZoomLinkLabel>
                                </ZoomLinkContainer> : null}


                        </Info>
                        <div>
                            <ButtonPrimary
                                variant={badgeVariant}
                                style={{
                                    pointerEvents: "none",
                                }}
                            >
                                {isOnline ? "Online" : class_type}
                            </ButtonPrimary>
                        </div>
                    </InfoContainer>

                </ClassInfo>
                <ExpandedDetails>
                    <ClassDateCard
                        date={classDate}
                        priceBreakdown={{
                            booking_total_amount: upcomingClass.booking_total_amount,
                            discount_amount: upcomingClass.discount_amount,
                            tax: upcomingClass.tax,
                            total_amount: upcomingClass.total_amount
                        }}
                        isOnline={isOnline}
                    />
                    {isRescheduled && (
                        <RescheduleTooltip
                            newDate={moment(upcomingClass.reschedule?.new_class_date).format(
                                "MMM D, YYYY"
                            )}
                            newTime={moment(
                                upcomingClass.reschedule?.new_class_time,
                                "HH:mm"
                            ).format("h:mm a")}
                        />
                    )}
                    <div>
                        <DetailCol>
                            <Label>Class instructions</Label>
                            <Instructions>{upcomingClass?.class_instruction}</Instructions>
                            {upcomingClass?.class_instruction.length > 100 && (
                                <ClassModal
                                    classData={{
                                        class: upcomingClass.class,
                                        rating: upcomingClass.rating,
                                        class_type: upcomingClass.class_type,
                                        date: classDate,
                                        class_instruction: upcomingClass.class_instruction,
                                        profile_pic: upcomingClass.teacher_profile_pic,
                                        online_type: upcomingClass.online_type,
                                    }}
                                />
                            )}
                        </DetailCol>

                        <Actions isEndAlign={isRescheduled}>
                            <ButtonPrimary
                                variant="secondary"
                                onClick={sendMessage}>
                                Message
                            </ButtonPrimary>
                            {!isRescheduled && (
                                <ButtonPrimary onClick={() => setIsOpenRescheduleModal(true)}>
                                    Re-Schedule
                                </ButtonPrimary>
                            )}
                            <ButtonPrimary
                                onClick={onCancel}
                                disabled={isLoading}
                                variant="primary_fade"
                            >
                                Cancel
                            </ButtonPrimary>
                        </Actions>
                    </div>
                </ExpandedDetails>
            </ClassItem>

            {isOpenRescheduleModal && (
                <RescheduleModal
                    show={isOpenRescheduleModal}
                    teacherUserId={upcomingClass.teacher_user_id}
                    closeModal={() => setIsOpenRescheduleModal(false)}
                    classType={upcomingClass.class_type}
                    students={upcomingClass.student_per_class}
                    teacherFirstName={upcomingClass.teacher_first_name}
                    nameOfClass={upcomingClass.class}
                    teacherPic={upcomingClass.teacher_profile_pic}
                    bookingSlot_id={upcomingClass.id}
                    booking_id={upcomingClass.booking_id}
                    booking_date={momentDateTime.format("YYYY-MM-DD")}
                    booking_time={momentDateTime.format("h:mm a")}
                    isOnline={isOnline}
                />
            )}
        </>
    );
};


const ClassItem = styled.div`
    border-width: 2px;
    border-style: solid;
    border-color: ${defaultThemeColors.lightBlue};
    background-color: ${defaultThemeColors.white};
    border-radius: 8px;
    padding: 1rem;
    width: 100%;
    display: flex;
    flex-direction: column;

    @media (width >= ${mediaSizes.md}) {
        padding: 1.5rem;
    }
`;

const ClassInfo = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
`;

const InfoContainer = styled.div`
    display: flex;
    justify-content: space-between;
    flex: 1;
`

const Info = styled.div`
    flex: 1;
    display: grid;
    gap: 0.5rem;
`;

const Name = styled.div`
    font-size: ${fonts.sizes.regular};
    font-weight: bold;
`;

const Details = styled.div`
    font-size: ${fonts.sizes.small};
    color: ${defaultThemeColors.placeholder};
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 0.3rem;

    & span:nth-of-type(2) {
        color: ${defaultThemeColors.txt};
    }
`;

const ExpandedDetails = styled.div`
    padding-top: 1rem;
    gap: 0.5rem;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    flex-grow: 1;
`;

const DetailCol = styled.div`
    display: grid;
    gap: 0.3rem;
    padding-bottom: 1rem;
`;

const Label = styled.span`
    color: ${defaultThemeColors.placeholder};
    font-size: ${fonts.sizes.small};
    font-weight: 500;
    white-space: nowrap;
    text-transform: capitalize;
`;

const Instructions = styled.div`
    color: ${defaultThemeColors.txt};
    font-size: ${fonts.sizes.small};
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Actions = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.6rem;

    > * {
        grid-column: span 1;
    }

    &:has(> *:nth-child(3)) > :first-child {
        grid-column: span 2;
    }

    > :only-child {
        grid-column: span 2;
    }
`;

const IconText = styled.div`
    display: flex;
    align-items: center;
    margin-left: 10px;
    font-size: 1rem;
    color: #ff827a;

    svg {
        margin-right: 8px;
    }
`;

const NameIconTextWrapper = styled.div`
    display: flex;
    align-items: center;
    width: fit-content;
    cursor: pointer;
`;

const ZoomLinkContainer = styled.p`
    display: flex;
    gap: 0.5rem;
`

const ClassLinkLabel = styled.p`
    color: ${defaultThemeColors.placeholder};
    font-size: ${fonts.sizes.small};
`

const ZoomLinkLabel = styled.a`
    color: ${defaultThemeColors.blue};
    font-size: ${fonts.sizes.small};
    font-weight: 500;
    white-space: nowrap;
    text-transform: capitalize;
    text-decoration: underline;
    cursor: pointer;
`